// vCards and the "save everyone" link that rides along in the first group text.
import { isApple } from './sms.js'

// --- link payload -----------------------------------------------------------
// The link is /c#<token>: 16 letters and digits. What it opens (everyone's name and number, and
// the group photo's token) is stored encrypted under that token (vault.js), so the server can't
// read it and the link stays short enough that no Messages app breaks it.
//
// Links sent before that carried the whole roster as base64url in the fragment. They still open.

const toB64url = (s) =>
  btoa(String.fromCharCode(...new TextEncoder().encode(s)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

const fromB64url = (s) =>
  new TextDecoder().decode(Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0)))

// Local calendar day (not UTC): a dinner at 9pm in California is still that day.
export const isoDay = (t) => {
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function encodeRoster({ created, members }) {
  return toB64url(JSON.stringify([1, isoDay(created), members.map((m) => [m.name, m.phone])]))
}

export function decodeRoster(s) {
  try {
    const [v, day, rows] = JSON.parse(fromB64url(s))
    if (v !== 1 || !Array.isArray(rows)) return null
    const members = rows
      .filter((r) => Array.isArray(r) && typeof r[0] === 'string' && /^\+\d{6,16}$/.test(r[1]))
      .map(([name, phone]) => ({ name: name.slice(0, 80), phone }))
    return { day: String(day).slice(0, 10), members }
  } catch {
    return null
  }
}

export const linkUrl = (token) => `${location.origin}/c#${token}`

// What /c#<token> opens: {n: name, p: phone, i: Instagram, f: face index into photo.faces}.
export function linkPayload({ created, photo }, members, faceOf) {
  return {
    v: 2,
    day: isoDay(created),
    members: members.map((m) => {
      const f = faceOf(m)
      return { n: m.name, p: m.phone, ...(m.ig ? { i: m.ig } : {}), ...(f == null ? {} : { f }) }
    }),
    ...(photo ? { photo: { t: photo.t, w: photo.w, h: photo.h, faces: photo.faces } } : {}),
  }
}

export function readPayload(p) {
  if (p?.v !== 2 || !Array.isArray(p.members)) return null
  const faces = Array.isArray(p.photo?.faces) ? p.photo.faces.filter((f) => Array.isArray(f) && f.length === 4 && f.every(Number.isFinite)) : []
  const photo = p.photo && /^[0-9A-Za-z]{16}$/.test(p.photo.t) ? { t: p.photo.t, w: p.photo.w, h: p.photo.h, faces } : null
  const members = p.members
    .filter((r) => r && typeof r.n === 'string' && /^\+\d{6,16}$/.test(r.p))
    .map((r) => ({
      name: r.n.slice(0, 80),
      phone: r.p,
      ...(typeof r.i === 'string' && /^[A-Za-z0-9._]{1,30}$/.test(r.i) ? { ig: r.i } : {}),
      ...(photo && Number.isInteger(r.f) && r.f >= 0 && r.f < faces.length ? { face: r.f } : {}),
    }))
  return { day: String(p.day ?? '').slice(0, 10), members, photo }
}

// --- vCard 3.0 ---------------------------------------------------------------

const esc = (s) => String(s).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1')

// RFC 2425 folding: long lines continue on the next line after a space. Splits on code points
// (not exact octets) so an emoji in a long name never gets cut in half.
const fold = (line) => {
  const chars = Array.from(line)
  if (chars.length <= 75) return line
  const parts = [chars.slice(0, 75).join('')]
  for (let i = 75; i < chars.length; i += 74) parts.push(' ' + chars.slice(i, i + 74).join(''))
  return parts.join('\r\n')
}

// `photo` is a base64 JPEG of their face from the group photo, so the contact shows who it is.
// An Instagram handle goes in as a social profile on iPhone (Contacts shows it with the Instagram
// label) and as a web address elsewhere, since Android's contacts app ignores social profiles.
function card({ name, phone, photo, ig }, { day, apple = isApple }) {
  const words = name.trim().split(/\s+/)
  const last = words.length > 1 ? words.pop() : ''
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${esc(name)}`,
    `N:${esc(last)};${esc(words.join(' '))};;;`,
    `TEL;TYPE=CELL:${phone}`,
    ...(ig ? [apple ? `X-SOCIALPROFILE;TYPE=instagram;X-USER=${ig}:https://www.instagram.com/${ig}` : `URL;TYPE=Instagram:https://www.instagram.com/${ig}`] : []),
    ...(photo ? [`PHOTO;ENCODING=b;TYPE=JPEG:${photo}`] : []),
    `NOTE:${esc(`From a group text, ${day}`)}`,
    'END:VCARD',
  ]
  return lines.map(fold).join('\r\n')
}

export const buildVcf = (members, meta) => members.map((m) => card(m, meta)).join('\r\n') + '\r\n'

export const vcfFilename = (day) => `group-text-${day}`

// "Sam Lee.vcf": what the card is called when it's sent as an attachment.
export const cardFilename = (name) => (name.replace(/[^\p{L}\p{N} .'-]/gu, '').trim().slice(0, 60) || 'contact') + '.vcf'

// The host can send everyone's card into the thread: one .vcf per person through the share
// sheet. Safari on iPhone can share vCards; Chrome on Android can't share that file type.
export function cardFiles(members, meta) {
  return members.map((m) => new File([buildVcf([m], meta)], cardFilename(m.name), { type: 'text/vcard' }))
}

export const canShareCards = () => {
  try {
    return !!navigator.canShare?.({ files: [new File(['BEGIN:VCARD\r\nEND:VCARD\r\n'], 'a.vcf', { type: 'text/vcard' })] })
  } catch {
    return false
  }
}

// --- getting the .vcf into the Contacts app ---------------------------------------
// Which of these shows "Add All Contacts" varies by OS and browser; the test lab exposes all.

export const VCF_METHODS = {
  post: 'Open via server echo (this tab)',
  'post-new': 'Open via server echo (new tab)',
  download: 'Download file',
  share: 'Share sheet with file',
}

export const defaultVcfMethod = 'post'

export async function deliverVcf(vcf, filename, method = defaultVcfMethod) {
  if (method === 'post' || method === 'post-new') {
    const form = Object.assign(document.createElement('form'), {
      method: 'POST',
      action: '/vcf',
      target: method === 'post-new' ? '_blank' : '_self',
    })
    form.hidden = true
    // <textarea>, not <input>: inputs strip the newlines a vCard is made of.
    for (const [name, value] of Object.entries({ vcf, name: filename })) {
      form.append(Object.assign(document.createElement('textarea'), { name, value }))
    }
    document.body.append(form)
    form.submit()
    form.remove()
    return
  }

  const file = new File([vcf], `${filename}.vcf`, { type: 'text/vcard' })
  if (method === 'share') {
    if (!navigator.canShare?.({ files: [file] })) throw new Error("This browser can't share files")
    await navigator.share({ files: [file] })
    return
  }

  const url = URL.createObjectURL(file)
  const a = Object.assign(document.createElement('a'), { href: url, download: file.name })
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
